import {
  carrierVehicleCount,
  languageSchema,
  nameSchema,
  referralCodeSchema,
  objectIdSchema,
  userDeviceSchema,
  UserSchema,
  vehicleQuantityAndTypeSchema,
  vehicleTypesSchema,
  emailSchema,
  optionalStringSchema,
  businessSettingsSchema,
  partyRoleSchema,
  orgSettingsSchema,
  type HeroPlatform,
} from '@hero/schema';
import { businessSchema, phoneSchema } from '@hero/schema';
import { z } from 'zod';
import {
  API,
  CustomerSignupData,
  SendOtpData,
  SignInData,
  SignUpData,
  carrier_company_driver,
  freelancer_driver,
  carrier_company_admin,
  individual_customer,
  business_customer,
  CarrierSignupData,
  ChangeUserLangData,
  CanUseAppData,
  AuthGetMeData,
} from '../types';

//* sign in
export const signInSchema = z.object({
  /**
   * the OTP code that the user has entered
   */
  code: z.string(),
  /**
   * OTP id
   */
  otpId: objectIdSchema,
  /**
   * user phone number
   */
  phone: phoneSchema,

  /** the token of the user device (Mobile) */
  deviceToken: userDeviceSchema.shape.token.optional(),
  /** this id is used to identify one user in one signal for notifications purposes */
  oneSignalId: UserSchema.shape.oneSignalId.optional(),
  /** the target org id to sing in to. This is used if the user has multiple orgs and wants to sign in in one of them */
  targetOrgId: objectIdSchema.optional(),
});

/** Sign in API for all type of users (carriers, customers, drivers, etc) */
export type SignInAPI = API<z.infer<typeof signInSchema>, SignInData>;

//* send OTP
export const sendOTPSchema = z.object({
  phone: phoneSchema,
});
export type SendOtpAPI = API<z.infer<typeof sendOTPSchema>, SendOtpData>;

//* signup
/** @deprecated common shape for legacy carrier/customer split signups. Replaced by {@link signUpSchema}. */
const signupSchema = UserSchema.data
  .pick({
    name: true,
  })
  .extend({
    /** the token of the user device (Mobile) */
    deviceToken: userDeviceSchema.shape.token.optional(),
    /** this id is used to identify one user in one signal for notifications purposes */
    oneSignalId: UserSchema.shape.oneSignalId.optional(),
    /** the carrier company / or customer company name  */
    companyName: z.coerce.string().optional(),
  });

//* signup - carrier
/** @deprecated use {@link signUpSchema} (`personType: 'driver' | 'company'`). */
export const carrierSignupSchema = signupSchema.extend({
  /** the user type of the carrier (Carrier Driver / Freelancer Driver / Carrier employee ) */
  userType: z.enum([carrier_company_driver, freelancer_driver, carrier_company_admin]),
  /** the carrier vehicles count ... if driver then just add one vehicle and set its count to 1 */
  vehiclesCount: carrierVehicleCount.shape.types,
});

/** @deprecated use {@link SignUpAPI}. */
export type CarrierSignupAPI = API<z.infer<typeof carrierSignupSchema>, CarrierSignupData>;

//* admin - create user
/** @deprecated use {@link adminCreateOrgSchema}. */
export const createUserAPISchema = signupSchema.extend({
  userType: z.enum([individual_customer, business_customer]),
  monthlyExpectedOrder: z.number().min(0).default(0),
  monthlyOrdersByVehicleType: z.array(vehicleQuantityAndTypeSchema).min(1),
  preferredLanguage: languageSchema,
  phone: phoneSchema,
  businessSettings: businessSettingsSchema.optional(),
});
/** @deprecated use {@link AdminCreateOrgAPI}. */
export type CreateUserAPI = API<
  z.infer<typeof createUserAPISchema>,
  Pick<CustomerSignupData, 'business' | 'user'>
>;

//* admin - create carrier
/** @deprecated use {@link adminCreateOrgSchema}. */
export const createCarrierAdminAPISchema = z.object({
  name: nameSchema,
  companyName: nameSchema,
  userType: z.enum([carrier_company_admin]),
  vehicles: carrierVehicleCount,
  preferredLanguage: languageSchema,
  phone: phoneSchema,
  email: optionalStringSchema(emailSchema),
});

/** @deprecated use {@link AdminCreateOrgAPI}. */
export type CreateCarrierAdminAPI = API<
  z.infer<typeof createCarrierAdminAPISchema>,
  Pick<CarrierSignupData, 'carrier' | 'user'>
>;

//* signup - customer
/** @deprecated use {@link signUpSchema} (`personType: 'individual_customer' | 'company'`). */
export const customerSignupSchema = createUserAPISchema
  .omit({ preferredLanguage: true, phone: true })
  .transform((data, ctx) => {
    const isCompanyAccount = data.userType === business_customer;

    if (isCompanyAccount) {
      const validCompanyName = nameSchema.safeParse(data.companyName);
      if (validCompanyName.success) {
        data.companyName = validCompanyName.data;
      } else {
        validCompanyName.error.issues.map(issue => {
          ctx.addIssue(issue);
        });
      }
    } else {
      // avoid empty string by mistake ''
      delete data.companyName;
    }
    return data;
  });

/** @deprecated use {@link SignUpAPI}. */
export type CustomerSignupAPI = API<z.infer<typeof customerSignupSchema>, CustomerSignupData>;

//* unified signup (wizard) — replaces customer/carrier signup endpoints
//
// The wizard is identical in every app (carrier-app, customer-app,
// carrier-dashboard, customer-dashboard). After OTP verification, a NEW
// user goes through three steps:
//
//   1. personType: company | driver | individual_customer
//   2. company  → company.roles[] (multi-select: business_customer / carrier_company / broker)
//      driver  → driverEmployment ('freelancer' | 'company')
//      indiv.  → just profile fields
//   3. profile / vehicles / company details (when applicable)
//
// The active app does NOT narrow choices; `canUseApp` redirects after
// sign-in if the wizard outcome doesn't match the app.
//
// See `docs/relations-v2/04-signup-linking-design.md` and
// `docs/relations-v2/08-user-identity.md`.

const signUpCommonShape = {
  /** display name of the person creating the account. */
  name: nameSchema,
  /** the token of the user device (Mobile only) */
  deviceToken: userDeviceSchema.shape.token.optional(),
  /** OneSignal id for push notifications. */
  oneSignalId: UserSchema.shape.oneSignalId.optional(),
  /** preferred UI language at signup. Defaults to current OTP/JWT language if omitted. */
  preferredLanguage: languageSchema.optional(),
  /**
   * The referral code the person is signing up **through** — the latest
   * `?r=` the browser saw (`hero_r_last`), falling back to the first
   * one. Resolved server-side to a `direct` stamp on `Org.meta.referral`
   * / `User.meta.referral`; an unknown code is ignored, never rejected.
   */
  referralCode: referralCodeSchema.optional(),
  /**
   * The first code the browser ever saw (`hero_r`), when it differs from
   * `referralCode`. Stored as `referral.originalCode` for history; the
   * signup link keeps ownership.
   */
  referralOriginalCode: referralCodeSchema.optional(),
  /** The `hero_vid` first-party cookie, so a cookie-only visit still attributes. */
  referralVisitorId: z.string().trim().max(64).optional(),
} as const;

/**
 * Subset of `partyRoleSchema` allowed for a Company-type signup. An Org
 * cannot be created with `individual_customer`, `driver`, or `contact`
 * roles — those are person-shaped, not company-shaped.
 */
export const companySignupRoleSchema = z.enum(['business_customer', 'carrier_company', 'broker']);
export type CompanySignupRole = z.infer<typeof companySignupRoleSchema>;

/**
 * Company profile sub-schema, shared between the `company` branch and
 * the `driver + driverEmployment: 'company'` branch (where the driver
 * also creates the company they work for).
 *
 * Vehicle / order data on this schema is **estimate-only** — the
 * wizard captures self-reported numbers that land on `Org.meta` (see
 * `org.meta.model.ts`). Actual fleet counts come from the `Vehicle`
 * collection once vehicles are added; live order volume comes from
 * the `Order` collection.
 */
export const signUpCompanyProfileSchema = z.object({
  /**
   * 1+ network roles the company plays. Multi-select (e.g. a single
   * Org that is BOTH `carrier_company` AND `broker`). Maps directly to
   * `Org.roles[]`.
   */
  roles: z.array(companySignupRoleSchema).min(1),
  companyName: nameSchema,
  /**
   * Vehicle types the org operates / books. Multi-select, no per-type
   * counts — that's intentional. Maps to `Org.meta.vehicleTypes[]`.
   * Required (≥ 1) when `roles` includes `carrier_company`.
   */
  vehicleTypes: z.array(vehicleTypesSchema).optional(),
  /**
   * Self-reported total vehicle count at signup. Maps to
   * `Org.meta.estimatedVehicleTotal`. The accurate count is later
   * derived from the `Vehicle` collection. Used only when `roles`
   * includes `carrier_company`.
   */
  totalVehicleCount: z.number().int().min(0).optional(),
  /**
   * Self-reported expected monthly shipment volume. Maps to
   * `Org.meta.estimatedOrdersPerMonth`. Used for any role that books
   * or handles capacity (business_customer / carrier_company / broker).
   */
  monthlyExpectedOrder: z.number().int().min(0).optional(),
  /** optional org settings overrides. */
  settings: orgSettingsSchema.partial().optional(),
});
export type SignUpCompanyProfile = z.infer<typeof signUpCompanyProfileSchema>;

export const signUpIndividualCustomerSchema = z.object({
  personType: z.literal('individual_customer'),
  ...signUpCommonShape,
});

export const signUpCompanySchema = z.object({
  personType: z.literal('company'),
  ...signUpCommonShape,
  company: signUpCompanyProfileSchema,
});

export const signUpDriverSchema = z.object({
  personType: z.literal('driver'),
  ...signUpCommonShape,
  driverEmployment: z.enum(['freelancer', 'company']),
  /**
   * required iff `driverEmployment === 'company'` — the driver also
   * creates the company they work for (typically `roles=[carrier_company]`).
   * Validated downstream in `OrgSignupService` because zod's
   * `discriminatedUnion` cannot express the intra-variant dependency.
   */
  company: signUpCompanyProfileSchema.optional(),
  /**
   * Vehicle types the driver operates. The driver UI picks a single
   * type but the schema stays an array so the company branch
   * (`driverEmployment: 'company'`) can pre-seed the new Org's
   * `meta.vehicleTypes` with whatever the driver drives.
   *
   * Required iff `driverEmployment === 'company'`, where it is the only
   * source for the new Org's fleet types — enforced downstream in
   * `OrgSignupService` for the same reason `company` is: zod's
   * `discriminatedUnion` cannot express an intra-variant dependency.
   *
   * A **freelancer** signup discards it (there is no Org to seed), so
   * the chat funnel does not ask: the body type is asked once, where it
   * is actually stored, when the truck is added.
   */
  vehicleTypes: z.array(vehicleTypesSchema).min(1).optional(),
});

/**
 * Unified signup payload — discriminated by `personType` (the wizard's
 * step 1). Replaces the separate `customerSignupSchema` and
 * `carrierSignupSchema`. Both legacy schemas are kept for the
 * deprecation window.
 */
export const signUpSchema = z.discriminatedUnion('personType', [
  signUpIndividualCustomerSchema,
  signUpCompanySchema,
  signUpDriverSchema,
]);
export type SignUpRequest = z.infer<typeof signUpSchema>;

/** Unified sign-up API. See `docs/relations-v2/08-user-identity.md`. */
export type SignUpAPI = API<SignUpRequest, SignUpData>;

//* unified admin-create-org — replaces /auth/admin-customer + /auth/admin-carrier.
// Same shape as `signUpSchema` but with `phone` + `email` so the admin
// can pre-provision an Org for a user that has not signed up yet.
export const adminCreateOrgSchema = signUpSchema.and(
  z.object({
    phone: phoneSchema,
    email: optionalStringSchema(emailSchema),
    /**
     * The WhatsApp thread staff are finishing the signup for. Same as the
     * assistant's `complete_signup`: the lead is linked to it and the guest
     * thread becomes this account's thread.
     */
    conversationId: objectIdSchema.optional(),
    /** Which product's funnel the account came through. */
    business: businessSchema.optional(),
  }),
);
export type AdminCreateOrgRequest = z.infer<typeof adminCreateOrgSchema>;
export type AdminCreateOrgAPI = API<AdminCreateOrgRequest, Pick<SignUpData, 'user' | 'org' | 'driver'>>;


//* sign out
export const signOutSchema = signupSchema.pick({ deviceToken: true, oneSignalId: true }).optional();

export type SignOutAPI = API<z.infer<typeof signOutSchema>, undefined>;

//* change user lang
export const changeUserLangSchema = z.object({
  /** the new language the user wants */
  language: languageSchema,
});

export type ChangeUserLangAPI = API<z.infer<typeof changeUserLangSchema>, ChangeUserLangData>;

// * delete
/** the data = true means the account has been deleted */
export type UserDeleteAccountAPI = API<undefined, boolean>;

// can use app
export type UserCanUseAppAPI = API<undefined, CanUseAppData>;

//* switch organization
export const switchOrgSchema = z.object({
  /** the target org id to switch to */
  targetOrgId: objectIdSchema.optional(),
  /** allowing users to sign in without org if they're considred freelancer or individual customer. */
  allowSignInWithoutOrg: z.boolean().default(false),
});

export type SwitchOrgAPI = API<z.infer<typeof switchOrgSchema>, SignInData>;

//* get current user identity
export type AuthGetMeAPI = API<undefined, AuthGetMeData>;

// ────────────────────────────────────────────────────────────────
// Auth-v2 refresh flow
// ────────────────────────────────────────────────────────────────

/**
 * `POST /auth/refresh` — rotate a refresh token.
 *
 * The refresh token is presented either as an HttpOnly cookie (web)
 * or in the body (mobile). On success, the server:
 *  - verifies the refresh JWT and looks up `RefreshSession`
 *  - detects reuse (already-consumed jti) and revokes the session
 *  - mints a fresh access + refresh pair; the refresh has a new jti
 *  - returns the access token in JSON; refresh in cookie (web) or JSON (mobile)
 */
export const refreshTokenSchema = z.object({
  /** Present on mobile — web sends via HttpOnly cookie. */
  refreshToken: z.string().optional(),
});
export type RefreshTokenRequest = z.infer<typeof refreshTokenSchema>;
export type RefreshTokenAPI = API<RefreshTokenRequest, {
  access_token: string;
  /** Present when the client is mobile. Web receives via Set-Cookie. */
  refresh_token?: string;
}>;

/**
 * `POST /auth/logout-all` — revoke every `RefreshSession` for the
 * current user. Every device using this account gets forced offline
 * on the next request.
 */
export type LogoutAllAPI = API<undefined, { revokedCount: number }>;

/**
 * `GET /auth/sessions` — device-management surface. Returns every
 * active session for the current user.
 */
export const listSessionsSchema = z.void();
export type SessionSummary = {
  id: string;
  device: {
    platform: HeroPlatform;
    userAgent?: string;
  };
  activeOrgId?: string;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
  isCurrent: boolean;
};
export type ListSessionsAPI = API<undefined, { sessions: SessionSummary[] }>;

/**
 * `POST /auth/sessions/:id/revoke` — revoke a single session (device
 * management UI's "sign out this device").
 */
export const revokeSessionSchema = z.object({
  sessionId: z.string(),
});
export type RevokeSessionAPI = API<z.infer<typeof revokeSessionSchema>, { revoked: boolean }>;
